import React, { useEffect, useMemo, useState } from "react";
import {
  cardThemes,
  defaultReportSections,
  reportSectionIds,
} from "./StatsCard.data";

const getInitialActionsBySection = (items) => {
  return items.reduce((acc, section) => {
    if (!section.actions?.length) {
      return acc;
    }

    const activeIndex = section.actions.findIndex((action) => action.active);
    acc[section.id] = activeIndex >= 0 ? activeIndex : 0;
    return acc;
  }, {});
};

const getVisibleCards = (section, activeActionsBySection) => {
  if (!section.actions?.length) {
    return section.cards || [];
  }

  const activeIndex = activeActionsBySection[section.id] ?? 0;
  const activeAction = section.actions[activeIndex];

  if (activeAction?.cards?.length) {
    return activeAction.cards;
  }

  if (
    section.cardsByAction &&
    activeAction?.key &&
    section.cardsByAction[activeAction.key]?.length
  ) {
    return section.cardsByAction[activeAction.key];
  }

  return section.cards || [];
};

const StatsCard = ({
  sections = defaultReportSections,
  sectionId = reportSectionIds.posicaoCarga,
  activeActionKey,
  onActionChange,
  onCardClick,
}) => {
  const visibleSections = useMemo(() => {
    if (!sectionId) {
      return sections;
    }

    return sections.filter((section) => section.id === sectionId);
  }, [sectionId, sections]);

  const [activeActionsBySection, setActiveActionsBySection] = useState(
    () => getInitialActionsBySection(visibleSections),
  );

  // On data refresh, preserve the user's current selection instead of
  // blindly resetting to the initial action (which caused desync with the
  // page-level activeActionKey state).
  useEffect(() => {
    setActiveActionsBySection((prev) => {
      const initial = getInitialActionsBySection(visibleSections);
      const result = { ...initial };
      for (const [id, prevIndex] of Object.entries(prev)) {
        const section = visibleSections.find((s) => s.id === id);
        if (section?.actions && prevIndex < section.actions.length) {
          result[id] = prevIndex;
        }
      }
      return result;
    });
  }, [visibleSections]);

  // Sync internal visual state when the parent controls the active action
  // (e.g. when vendedorSelecionado changes and the page resets to "rota").
  useEffect(() => {
    if (!activeActionKey || !sectionId) return;
    const section = visibleSections.find((s) => s.id === sectionId);
    if (!section?.actions) return;
    const actionIndex = section.actions.findIndex((a) => a.key === activeActionKey);
    if (actionIndex < 0) return;
    setActiveActionsBySection((prev) => {
      if (prev[sectionId] === actionIndex) return prev;
      return { ...prev, [sectionId]: actionIndex };
    });
  }, [activeActionKey, sectionId, visibleSections]);

  const sectionsWithCards = useMemo(() => {
    return visibleSections.map((section) => ({
      ...section,
      visibleCards: getVisibleCards(section, activeActionsBySection),
    }));
  }, [activeActionsBySection, visibleSections]);

  return (
    <div className="container ">
      <div className="w-full  mx-auto py-6 space-y-4 ">
        {sectionsWithCards.map((section) => {
          const { visibleCards } = section;
          const activeIndex = activeActionsBySection[section.id] ?? 0;
          const activeAction = section.actions?.[activeIndex];
          const hasOwnPeriod =
            activeAction && Object.prototype.hasOwnProperty.call(activeAction, "period");
          const activePeriod = hasOwnPeriod ? activeAction?.period : section.period;
          const showPeriod = Boolean(activePeriod);

          return (
          <section
            key={section.id}
            className="rounded-md shadow  border-gray-200 bg-white p-6"
          >
            <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <h2 className="text-[clamp(1rem,1.8vw,1.3125rem)] font-bold leading-none text-[#0A2E73]">
                <span className="text-[#0A2E73]">
                  {section.title}
                  {showPeriod ? "" : ""}
                </span>

              </h2>

              {!!section.actions?.length && (
                <div className="grid grid-cols-2 gap-2 md:w-auto">
                  {section.actions.map((action, actionIndex) => {
                    const isActive =
                      (activeActionsBySection[section.id] ?? 0) === actionIndex;

                    return (
                    <button
                      key={action.label}
                      type="button"
                      onClick={() => {
                        setActiveActionsBySection((current) => ({
                          ...current,
                          [section.id]: actionIndex,
                        }));
                        if (onActionChange) {
                          onActionChange(section.id, action.key);
                        }
                      }}
                      className={`h-10 min-w-[150px] rounded-md border px-4 text-sm font-semibold transition-colors ${
                        isActive
                          ? "border-[#0655A3] bg-[#0655A3] text-white"
                          : "border-[#B9C2CF] bg-[#F7F7F7] text-[#1A2B49]"
                      }`}
                    >
                      {action.label}
                    </button>
                    );
                  })}
                </div>
              )}
            </header>

            <div
              className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))] min-[800px]:[grid-template-columns:repeat(var(--cards-count),minmax(0,1fr))]"
              style={{
                "--cards-count": visibleCards.length,
              }}
            >
              {visibleCards.map((card) => {
                const isClickable = !!(card.filterKey && onCardClick);
                const Tag = isClickable ? "button" : "article";

                return (
                  <Tag
                    key={`${section.id}-${card.title}`}
                    type={isClickable ? "button" : undefined}
                    onClick={isClickable ? () => onCardClick(card) : undefined}
                    className={`flex min-h-[120px] w-full flex-col items-center justify-center rounded-md px-4 py-3 text-center ${
                      cardThemes[card.color] || cardThemes.navy
                    }${isClickable ? " cursor-pointer transition-opacity hover:opacity-90 active:opacity-80" : ""}`}
                  >
                    <p className="mb-2 text-[clamp(0.75rem,1vw,0.875rem)] font-bold leading-tight">
                      {card.title}
                    </p>

                    <h3 className="break-words text-[clamp(1.375rem,2.4vw,1.5625rem)] font-bold leading-none">
                      {card.value}
                    </h3>

                    {!!card.subtitle && (
                      <span className="mt-2 text-[clamp(0.8125rem,1.05vw,0.9375rem)] font-semibold">
                        {card.subtitle}
                      </span>
                    )}
                  </Tag>
                );
              })}
            </div>
          </section>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(StatsCard);
